import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AppSettings } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { api, extractError } from '../../lib/api';
import { useToast } from '../../components/admin/Toast';

const inputClass =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover disabled:opacity-50';
const dangerBtn =
  'rounded-md border border-admin-danger bg-white px-3 py-2 text-sm text-admin-danger hover:bg-admin-cream disabled:opacity-50';

export default function AdminEmailSettingsPage(): JSX.Element {
  const toast = useToast();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [reviewUrl, setReviewUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadSettings = async (): Promise<void> => {
    const res = await api.get<AppSettings>('/admin/settings');
    setLogoUrl(res.data.emailLogoUrl ?? null);
    setReviewUrl(res.data.reviewUrl ?? '');
  };

  useEffect(() => {
    void (async () => {
      try {
        await loadSettings();
      } catch (err) {
        toast.error(extractError(err, 'Falha ao carregar configurações'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogoUpload = async (file: File): Promise<void> => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('logo', file);
      const res = await api.post<{ url: string }>('/admin/email-settings/logo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoUrl(res.data.url);
      toast.success('Logo atualizado.');
    } catch (err) {
      toast.error(extractError(err, 'Falha ao enviar logo'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLogoRemove = async (): Promise<void> => {
    if (!window.confirm('Remover o logo atual? Os emails passam a usar o nome da loja em texto.')) {
      return;
    }
    setUploading(true);
    try {
      await api.delete('/admin/email-settings/logo');
      setLogoUrl(null);
      toast.success('Logo removido.');
    } catch (err) {
      toast.error(extractError(err, 'Falha ao remover logo'));
    } finally {
      setUploading(false);
    }
  };

  const handleReviewSave = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setSavingReview(true);
    try {
      const res = await api.put<AppSettings>('/admin/settings', {
        reviewUrl: reviewUrl.trim() === '' ? null : reviewUrl.trim(),
      });
      setReviewUrl(res.data.reviewUrl ?? '');
      toast.success('URL de avaliação guardado.');
    } catch (err) {
      toast.error(extractError(err, 'Falha ao guardar URL de avaliação'));
    } finally {
      setSavingReview(false);
    }
  };

  if (loading) return <p className="text-admin-text-soft">A carregar…</p>;

  return (
    <>
      <SEO title="Emails — Configurações" noindex />
      <div className="mb-2 text-sm">
        <Link to="/admin/emails" className="text-admin-primary hover:underline">
          ← Templates
        </Link>
      </div>
      <h1>Configurações de email</h1>
      <p className="mt-1 mb-6 text-admin-text-soft">
        Estas definições aplicam-se a <strong>todos</strong> os templates.
      </p>

      <section className="mb-10 max-w-2xl">
        <h2 className="mb-2">Logo</h2>
        <p className="mb-4 text-sm text-admin-text-soft">
          O logo aparece no cabeçalho de todos os emails. Será convertido para PNG e
          guardado no R2. Recomendado: imagem com fundo transparente, pelo menos 360×96 px.
          Se não tiveres logo, o cabeçalho mostra o nome da loja em texto.
        </p>
        <div
          className="rounded-lg border border-admin-border bg-white p-6 text-center"
          style={{ borderBottom: '3px solid #8b1e1e' }}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Logo atual"
              style={{ maxHeight: 64, maxWidth: 280, display: 'inline-block' }}
            />
          ) : (
            <span
              style={{
                fontFamily: 'Georgia, serif',
                fontSize: 24,
                letterSpacing: 4,
                color: '#111',
                textTransform: 'uppercase',
              }}
            >
              Rosita Store
            </span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleLogoUpload(file);
            }}
            disabled={uploading}
            className="text-sm"
          />
          {logoUrl ? (
            <button
              type="button"
              onClick={() => {
                void handleLogoRemove();
              }}
              disabled={uploading}
              className={dangerBtn}
            >
              Remover logo
            </button>
          ) : null}
        </div>
        {uploading ? (
          <p className="mt-2 text-sm text-admin-muted">A processar…</p>
        ) : null}
      </section>

      <section className="max-w-2xl">
        <h2 className="mb-2">URL de avaliação</h2>
        <p className="mb-4 text-sm text-admin-text-soft">
          Se definido, o email enviado quando uma encomenda é marcada como{' '}
          <strong>entregue</strong> inclui um botão a pedir a avaliação (Google,
          Trustpilot, etc.). Deixar em branco para omitir.
        </p>
        <form onSubmit={handleReviewSave} className="flex flex-wrap items-start gap-2">
          <input
            type="url"
            value={reviewUrl}
            onChange={(e) => setReviewUrl(e.target.value)}
            placeholder="https://g.page/r/..."
            className={`${inputClass} flex-1 min-w-[280px]`}
          />
          <button type="submit" disabled={savingReview} className={primaryBtn}>
            {savingReview ? 'A guardar…' : 'Guardar'}
          </button>
        </form>
      </section>
    </>
  );
}
